import React, { useEffect, useRef, useState } from "react";
import Sidebar from "../../components/Admin/Sidebar";
import Header from "../../components/Admin/Header";
import api from "../../api/axois";
import layout from "./Payments.module.css";
import bookingStyles from "./BookingManagement.module.css";
import styles from "./AdminGallery.module.css";
import { FaPlus, FaTrash, FaEdit, FaTimes, FaGripVertical } from "react-icons/fa";

const CATEGORIES = ["general", "rooms", "amenities", "exterior", "dining"];

const emptyForm = { caption: "", category: "general", sort_order: 0, is_active: true };

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Upload modal
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadForm, setUploadForm] = useState({ caption: "", category: "general", sort_order: "" });
  const [uploading, setUploading] = useState(false);
  const uploadRef = useRef();

  // Edit modal
  const [editImage, setEditImage] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editFile, setEditFile] = useState(null);
  const [editPreview, setEditPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const editFileRef = useRef();

  // Filter
  const [filterCategory, setFilterCategory] = useState("all");

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

  const loadImages = () => {
    setLoading(true);
    api.get("/admin/gallery")
      .then(res => setImages(res.data.images || []))
      .catch(() => setError("Failed to load gallery images."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadImages();
  }, []);

  const resolveUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return API_BASE.replace(/\/api$/, "") + url;
  };

  // ── Upload ────────────────────────────────────────────────────────────────
  const openUpload = () => {
    setUploadFiles([]);
    setUploadForm({ caption: "", category: "general", sort_order: "" });
    setUploadOpen(true);
  };

  const handleUploadFileChange = (e) => {
    setUploadFiles(Array.from(e.target.files));
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadFiles.length) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      uploadFiles.forEach(f => fd.append("images[]", f));
      if (uploadForm.caption) fd.append("caption", uploadForm.caption);
      fd.append("category", uploadForm.category);
      if (uploadForm.sort_order !== "") fd.append("sort_order", uploadForm.sort_order);

      await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSuccess(`${uploadFiles.length} image(s) uploaded.`);
      setUploadOpen(false);
      loadImages();
    } catch (err) {
      setError(err.response?.data?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  // ── Edit ─────────────────────────────────────────────────────────────────
  const openEdit = (img) => {
    setEditImage(img);
    setEditForm({
      caption: img.caption || "",
      category: img.category || "general",
      sort_order: img.sort_order ?? 0,
      is_active: img.is_active,
    });
    setEditFile(null);
    setEditPreview(null);
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditFile(file);
      setEditPreview(URL.createObjectURL(file));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("caption", editForm.caption);
      fd.append("category", editForm.category);
      fd.append("sort_order", editForm.sort_order);
      fd.append("is_active", editForm.is_active ? "1" : "0");
      if (editFile) fd.append("image", editFile);

      await api.post(`/admin/gallery/${editImage.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess("Image updated.");
      setEditImage(null);
      loadImages();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update image.");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this image? This cannot be undone.")) return;
    setError("");
    try {
      await api.delete(`/admin/gallery/${id}`);
      setSuccess("Image deleted.");
      setImages(prev => prev.filter(img => img.id !== id));
    } catch {
      setError("Failed to delete image.");
    }
  };

  // ── Reorder (simple up/down buttons) ─────────────────────────────────────
  const moveImage = async (index, direction) => {
    const newImages = [...images];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newImages.length) return;
    [newImages[index], newImages[swapIndex]] = [newImages[swapIndex], newImages[index]];
    const order = newImages.map((img, i) => ({ id: img.id, sort_order: i }));
    setImages(newImages);
    try {
      await api.post("/admin/gallery/reorder", { order });
    } catch {
      setError("Failed to save new order.");
      loadImages(); // revert
    }
  };

  const filtered = filterCategory === "all"
    ? images
    : images.filter(img => img.category === filterCategory);

  const allCategories = ["all", ...CATEGORIES, ...images.map(i => i.category).filter(c => !CATEGORIES.includes(c))];
  const uniqueCategories = [...new Set(allCategories)];

  return (
    <div className={layout.container}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {sidebarOpen && <div className={layout.overlay} onClick={() => setSidebarOpen(false)} />}

      <div className={layout.content}>
        <Header onMenuClick={() => setSidebarOpen(true)} />

        <div className={styles.wrapper}>
          {/* Top bar */}
          <div className={styles.topBar}>
            <h2 className={styles.heading}>Gallery Management</h2>
            <button className={bookingStyles.manualBookBtn} onClick={openUpload}>
              <FaPlus className={bookingStyles.plusIcon} />
              Upload Images
            </button>
          </div>

          {error && <div className={styles.errorMsg}>{error}</div>}
          {success && <div className={styles.successMsg}>{success}</div>}

          {/* Category filter */}
          <div className={styles.filterBar}>
            {uniqueCategories.map(cat => (
              <button
                key={cat}
                className={`${styles.filterBtn} ${filterCategory === cat ? styles.filterBtnActive : ""}`}
                onClick={() => setFilterCategory(cat)}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>

          {/* Grid */}
          {loading ? (
            <div className={styles.center}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div className={styles.center}>No images found. Upload some to get started.</div>
          ) : (
            <div className={styles.grid}>
              {filtered.map((img, index) => (
                <div key={img.id} className={`${styles.card} ${!img.is_active ? styles.cardInactive : ""}`}>
                  <img
                    src={resolveUrl(img.url)}
                    alt={img.caption || "Gallery image"}
                    className={styles.cardImg}
                  />
                  <div className={styles.cardBody}>
                    <span className={styles.cardCategory}>{img.category}</span>
                    {img.caption && <p className={styles.cardCaption}>{img.caption}</p>}
                    {!img.is_active && <span className={styles.inactiveBadge}>Inactive</span>}
                  </div>
                  <div className={styles.cardActions}>
                    <button className={styles.iconBtn} title="Move up" onClick={() => moveImage(index, -1)} disabled={index === 0}>
                      ↑
                    </button>
                    <button className={styles.iconBtn} title="Move down" onClick={() => moveImage(index, 1)} disabled={index === filtered.length - 1}>
                      ↓
                    </button>
                    <button className={`${styles.iconBtn} ${styles.editBtn}`} title="Edit" onClick={() => openEdit(img)}>
                      <FaEdit />
                    </button>
                    <button className={`${styles.iconBtn} ${styles.deleteBtn}`} title="Delete" onClick={() => handleDelete(img.id)}>
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Upload Modal ─────────────────────────────────────────────────── */}
      {uploadOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3>Upload Gallery Images</h3>
              <button className={styles.modalClose} onClick={() => setUploadOpen(false)}><FaTimes /></button>
            </div>
            <form onSubmit={handleUploadSubmit}>
              <div className={styles.formGroup}>
                <label>Images <span className={styles.required}>*</span></label>
                <input
                  ref={uploadRef}
                  type="file"
                  accept="image/*"
                  multiple
                  required
                  onChange={handleUploadFileChange}
                  className={styles.fileInput}
                />
                {uploadFiles.length > 0 && (
                  <div className={styles.previewRow}>
                    {uploadFiles.map((f, i) => (
                      <img key={i} src={URL.createObjectURL(f)} alt="" className={styles.previewThumb} />
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label>Category</label>
                <select
                  value={uploadForm.category}
                  onChange={e => setUploadForm(p => ({ ...p, category: e.target.value }))}
                  className={styles.input}
                >
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Caption <span className={styles.optional}>(optional)</span></label>
                <input
                  type="text"
                  value={uploadForm.caption}
                  onChange={e => setUploadForm(p => ({ ...p, caption: e.target.value }))}
                  placeholder="Shared caption for all uploaded images"
                  className={styles.input}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Sort Order <span className={styles.optional}>(optional)</span></label>
                <input
                  type="number"
                  min="0"
                  value={uploadForm.sort_order}
                  onChange={e => setUploadForm(p => ({ ...p, sort_order: e.target.value }))}
                  placeholder="0"
                  className={styles.input}
                />
              </div>

              <div className={styles.modalActions}>
                <button type="button" className={styles.cancelBtn} onClick={() => setUploadOpen(false)}>Cancel</button>
                <button type="submit" className={styles.saveBtn} disabled={uploading}>
                  {uploading ? "Uploading..." : "Upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Modal ───────────────────────────────────────────────────── */}
      {editImage && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3>Edit Image</h3>
              <button className={styles.modalClose} onClick={() => setEditImage(null)}><FaTimes /></button>
            </div>
            <form onSubmit={handleEditSubmit}>
              {/* Current / new image preview */}
              <div className={styles.editPreviewWrap}>
                <img
                  src={editPreview || resolveUrl(editImage.url)}
                  alt="preview"
                  className={styles.editPreview}
                />
                <button
                  type="button"
                  className={styles.replaceBtn}
                  onClick={() => editFileRef.current.click()}
                >
                  Replace Image
                </button>
                <input
                  ref={editFileRef}
                  type="file"
                  accept="image/*"
                  className={styles.hiddenInput}
                  onChange={handleEditFileChange}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Caption</label>
                <input
                  type="text"
                  value={editForm.caption}
                  onChange={e => setEditForm(p => ({ ...p, caption: e.target.value }))}
                  className={styles.input}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Category</label>
                <select
                  value={editForm.category}
                  onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))}
                  className={styles.input}
                >
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Sort Order</label>
                <input
                  type="number"
                  min="0"
                  value={editForm.sort_order}
                  onChange={e => setEditForm(p => ({ ...p, sort_order: e.target.value }))}
                  className={styles.input}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.toggleLabel}>
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={e => setEditForm(p => ({ ...p, is_active: e.target.checked }))}
                  />
                  <span>Active (visible on public gallery)</span>
                </label>
              </div>

              <div className={styles.modalActions}>
                <button type="button" className={styles.cancelBtn} onClick={() => setEditImage(null)}>Cancel</button>
                <button type="submit" className={styles.saveBtn} disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
